import React from 'react'
import { ComplianceFramework } from '@trustero/trustero-api-web/lib/audit/framework_pb'
import {
  useHideModal,
  useIsShowModal,
  useModalState,
} from 'src/Modal/ModalStateContext'
import { E2E_IDS } from 'src/Utils/testIds'
import { TextButton } from 'src/components/Reusable/Buttons/TextButton'
import { FRAMEWORK_MODEL_IDS } from 'src/Utils/globalEnums'
import { useHasFrameworksPage } from 'src/context/Gatekeeper'
import { CircleCheckSVG } from '../../../Icons/Basic'
import { ModalForm, ModalFormId } from '../..'
import { ModalFooter } from '../../ModalForm.styles'
import { useFrameworkWizard } from '../../../../context/FormContext'
import {
  ComplianceLogoComponent,
  FrameworkModelIdToComplianceLogo,
} from '../../../Icons/ComplianceLogos'
import { useComplianceFrameworks } from '../../../async/complianceframework/useComplianceFrameworks'
import { OpenModalWrapper } from '../../ModalButtons'
import { ReusableControlsModal } from './Controls/ReusableControlsModal'
import { AddCustomFrameworkModal } from './AddCustomFrameworkModal'
import {
  EnabledFrameworkCard,
  ComplianceFrameworkModalTitle,
  ComplianceFrameworkModalDescriptionWhite,
  ComplianceFrameworkCardContainer,
  ComplianceFrameworkModalDescription,
  SVG_STYLES,
  ModalFormContainer,
  FrameworkContainer,
} from './ConfirmFrameworkModal.styles'

export const ChooseFrameworkModal = (): JSX.Element => {
  const { openModal } = useModalState()
  const { data, isLoading } = useComplianceFrameworks()
  const { hasAccess: hasFrameworksIndexPage } = useHasFrameworksPage()
  const {
    frameworkState: { setFramework },
  } = useFrameworkWizard()
  const show = useIsShowModal(ModalFormId.CHOOSE_COMPLIANCE_FRAMEWORK)
  const hide = useHideModal({
    modalId: ModalFormId.CHOOSE_COMPLIANCE_FRAMEWORK,
  })

  if (isLoading || !data) {
    return <></>
  }

  const frameworks = data
    .getItemsList()
    .sort((a, b) => a.getName().localeCompare(b.getName()))

  const openCustomModal = () => {
    hide()
    openModal(ModalFormId.ADD_CUSTOM_FRAMEWORK)
  }

  const frameworkOptions = frameworks.filter(
    (framework: ComplianceFramework) =>
      framework.getModelId() !== FRAMEWORK_MODEL_IDS.SOC1 && // filter out SOC1 because it is customized for a specific client
      framework.getModelId() !== FRAMEWORK_MODEL_IDS.NYDFS,
  )

  return (
    <div>
      <ReusableControlsModal />
      <AddCustomFrameworkModal />
      <ModalForm
        hide={hide}
        show={show}
        formId={ModalFormId.CHOOSE_COMPLIANCE_FRAMEWORK}
        size="xl"
        hideButtons
        title="Add Compliance Framework"
        description="Expand your compliance posture."
        customFooter={
          hasFrameworksIndexPage ? undefined : (
            <ModalFooter>
              <span>Need another framework?&nbsp;</span>
              <TextButton onClick={openCustomModal}>
                Add a custom framework
              </TextButton>
            </ModalFooter>
          )
        }
      >
        <ModalFormContainer id={ModalFormId.CHOOSE_COMPLIANCE_FRAMEWORK}>
          <FrameworkContainer>
            {frameworkOptions.map((framework: ComplianceFramework) =>
              framework.getEnabled() ? (
                <EnabledFrameworkCard
                  key={framework.getModelId()}
                  onClick={() => setFramework(framework)}
                >
                  <div style={SVG_STYLES}>
                    <CircleCheckSVG />
                  </div>
                  <ComplianceFrameworkModalTitle>
                    {framework.getName()}
                  </ComplianceFrameworkModalTitle>
                  <ComplianceFrameworkModalDescriptionWhite>
                    {framework.getDescription()}
                  </ComplianceFrameworkModalDescriptionWhite>
                </EnabledFrameworkCard>
              ) : (
                <OpenModalWrapper
                  key={framework.getModelId()}
                  onClick={() => setFramework(framework)}
                  modalFormId={ModalFormId.REUSABLE_CONTROLS}
                >
                  <ComplianceFrameworkCardContainer
                    data-testid={E2E_IDS.FRAMEWORKS_CARD(
                      framework.getModelId(),
                    )}
                  >
                    <ComplianceLogoComponent
                      complianceLogo={
                        FrameworkModelIdToComplianceLogo[
                          framework.getModelId() as FRAMEWORK_MODEL_IDS
                        ]
                      }
                      styles={SVG_STYLES}
                    />
                    <ComplianceFrameworkModalTitle>
                      {framework.getName()}
                    </ComplianceFrameworkModalTitle>
                    <ComplianceFrameworkModalDescription>
                      {framework.getDescription()}
                    </ComplianceFrameworkModalDescription>
                  </ComplianceFrameworkCardContainer>
                </OpenModalWrapper>
              ),
            )}
          </FrameworkContainer>
        </ModalFormContainer>
      </ModalForm>
    </div>
  )
}
